import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import type { Company } from "@/types/company.type";
import { useEffect, useState } from "react";
import { useCreateCompany, useUpdateCompany } from "@/hooks/useCompanies";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { Switch } from "../ui/switch";
import { showErrorToast, showSuccessToast } from "../ui/toast";

const comanySchema = z.object({
  name: z
    .string()
    .min(3, "Le nom de l'entreprise doit contenir au moins 3 caractères"),
  email: z.string().email("L'email doit être une adresse email valide"),
  siret: z.string().optional(),
  isActive: z.boolean().optional(),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  phoneNumber: z.string().optional(),
  pointeuseLogin: z.string().optional(),
  pointeusePassword: z.string().optional(),
});

type CompanyFormData = z.infer<typeof comanySchema>;

interface CompanyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: Company | null;
  onSuccess?: () => void; // Fonction à appeler lorsque la création/mise à jour est réussie (pour fermer le dialog, etc.)
}

export const CompanyFormDialog = ({
  open,
  onOpenChange,
  company,
  onSuccess,
}: CompanyFormDialogProps) => {
  const isEditing = !!company; // true si company est défini, false sinon
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 2;


  // Hooks React Query pour créer et modifier
  const { mutate: createCompanyMutation, isPending: isCreating } =
    useCreateCompany();
  const { mutate: updateCompanyMutation, isPending: isUpdating } =
    useUpdateCompany();

  const form = useForm<CompanyFormData>({
    resolver: zodResolver(comanySchema),
    defaultValues: {
      name: company?.name || "",
      email: company?.email || "",
      siret: company?.siret || "",
      isActive: company?.isActive,
      address: company?.address || "",
      postalCode: company?.postalCode || "",
      city: company?.city || "",
      phoneNumber: company?.phoneNumber || "",
      pointeuseLogin: company?.pointeuseLogin || "",
      pointeusePassword: "",
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    setCurrentStep(1); // Réinitialiser le step à 1 lorsque le dialog s'ouvre
    if (company) {
      // Mode édition : remplir avec les données de l'entreprise
      reset({
        name: company.name,
        email: company.email,
        siret: company.siret || "",
        isActive: company.isActive,
        address: company.address || "",
        postalCode: company.postalCode || "",
        city: company.city || "",
        phoneNumber: company.phoneNumber || "",
        pointeuseLogin: company.pointeuseLogin || "",
        pointeusePassword: "",
      });
    } else {
      // Mode création : vider le formulaire
      form.reset({
        name: "",
        email: "",
        siret: "",
        isActive: true,
        address: "",
        postalCode: "",
        phoneNumber: "",
        pointeuseLogin: "",
        pointeusePassword: "",
      });
    }
  }, [company, form]);

  const handleNextStep = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const isValid = await form.trigger(["name", "email"]);
    if (isValid) {
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentStep(1);
  };

  const handleCancel = () => {
    // Réinitialiser le formulaire
    form.reset();
    // Remettre l'étape à 1
    setCurrentStep(1);
    // Fermer le dialog
    onOpenChange(false);
  };

  // Wrapper pour gérer la fermeture du dialog avec reset
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Si le dialog se ferme, réinitialiser le formulaire
      form.reset();
      setCurrentStep(1);
    }
    onOpenChange(open);
  };

  const onSubmit = (data: CompanyFormData) => {
    // Empêcher la soumission si on n'est pas à la dernière étape
    if (currentStep < totalSteps) {
      return;
    }

    if (isEditing) {
      // Modification d'entreprise
      updateCompanyMutation(
        { id: company.id, data },
        {
          onSuccess: () => {
            onOpenChange(false);
            onSuccess?.();
            form.reset();
            showSuccessToast("Entreprise modifiée avec succès");
          },
          onError: (error: Error) => {
            showErrorToast(
              error.message || "Une erreur est survenue lors de la modification"
            );
          },
        }
      );
    } else {
      // Création d'entreprise
      createCompanyMutation(data, {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
          form.reset();
          showSuccessToast("Entreprise créée avec succès");
        },
        onError: (error: Error) => {
          showErrorToast(
            error.message || "Une erreur est survenue lors de la création"
          );
        },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[calc(100%-1rem)] sm:max-w-[600px] md:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Modifier l'entreprise" : "Nouvelle entreprise"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Modifiez les informations de l'entreprise."
              : "Ajoutez une nouvelle entreprise au système."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            {/* Indicateur d'étapes */}
            <div className="flex items-center justify-center mb-2 sm:mb-4">
              <div className="flex items-center gap-1 sm:gap-2">
                <div
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
                    currentStep >= 1
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  1
                </div>
                <div className="h-1 w-8 sm:w-12 bg-muted">
                  <div
                    className={`h-full transition-all ${
                      currentStep >= 2 ? "bg-primary" : "bg-muted"
                    }`}
                  />
                </div>
                <div
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
                    currentStep >= 2
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  2
                </div>
              </div>
            </div>

            {/* Étape 1 : Informations générales */}
            {currentStep === 1 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mx-auto mb-4 mt-4 sm:mt-6 border rounded-lg p-4 sm:p-6">
                <FormField
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm sm:text-base font-semibold">
                        Nom *
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Nom de l'entreprise" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm sm:text-base font-semibold">
                        Email *
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="email@entreprise.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="siret"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SIRET</FormLabel>
                      <FormControl>
                        <Input placeholder="12345678901234" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Téléphone</FormLabel>
                      <FormControl>
                        <Input placeholder="0612345678" type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Adresse</FormLabel>
                      <FormControl>
                        <Input placeholder="123 Rue Example" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code postal</FormLabel>
                      <FormControl>
                        <Input placeholder="75001" type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ville</FormLabel>
                      <FormControl>
                        <Input placeholder="Paris" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                
              </div>
            )}

            {/* Étape 2 : Identifiants pointeuse */}
            {currentStep === 2 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mx-auto mb-4 mt-4 sm:mt-6 border rounded-lg p-4 sm:p-6">
                <FormField
                  control={control}
                  name="pointeuseLogin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Login Pointeuse</FormLabel>
                      <FormControl>
                        <Input placeholder="login-pointeuse" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="pointeusePassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mot de passe Pointeuse</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Laisser vide pour générer automatiquement"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="col-span-1 sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-lg border p-3 sm:p-4">
                      <div className="space-y-0.5 flex-1">
                        <FormLabel className="text-sm sm:text-base font-semibold">
                          Statut
                        </FormLabel>
                        <div className="text-xs sm:text-sm text-muted-foreground">
                          L'entreprise est active et peut utiliser le système
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Footer avec navigation */}
            <DialogFooter className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-2">
              <span className="text-xs sm:text-sm text-muted-foreground order-3 sm:order-1 text-center sm:text-left">
                Étape {currentStep} sur {totalSteps}
              </span>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto order-1 sm:order-2">
                {/* Boutons principaux en premier sur mobile */}
                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    className="w-full sm:w-auto order-1 sm:order-3"
                  >
                    Suivant
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isCreating || isUpdating || !isDirty}
                    className="w-full sm:w-auto order-1 sm:order-3"
                  >
                    {isCreating || isUpdating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        En cours...
                      </>
                    ) : isEditing ? (
                      "Modifier"
                    ) : (
                      "Créer"
                    )}
                  </Button>
                )}

                {/* Boutons secondaires après sur mobile */}
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePreviousStep}
                    className="w-full sm:w-auto order-2 sm:order-2"
                  >
                    Précédent
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  className="w-full sm:w-auto order-3 sm:order-1"
                >
                  Annuler
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
